import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EqualizerIcon from '@mui/icons-material/InsertChartOutlined';

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState';
import toFixed from '../lib/toFixed';
import ScatterPlot from './ScatterPlot'

// Include the linearRegression function provided earlier
import { multipleLinearRegression } from '../lib/stats';

function LinearRegression() {
  const [independentVariables, setIndependentVariables] = useAutoSaveState('independentVariables', [
    { name: 'X1', data: '', key: 0 },
    { name: 'X2', data: '', key: 1 },
  ]);
  const [dependentVariable, setDependentVariable] = useAutoSaveState('dependentVariable', { name: 'Y', data: '' });
  const [points, setPoints] = useState([])
  const [result, setResult] = useState({});

  const addIndependentVariable = () => {
    setIndependentVariables((prevVars) => [
      ...prevVars,
      { name: `X${prevVars.length + 1}`, data: '', key: Math.random().toString(36).substr(2, 9)},
    ]);
  };

  const removeIndependentVariable = (key) => {
    setIndependentVariables((prevVars) => prevVars.filter((variable) => variable.key !== key));
  };

  const handleVariableNameChange = (key, newName) => {
    setIndependentVariables((prevVars) =>
      prevVars.map((variable) =>
        variable.key === key ? { ...variable, name: newName } : variable
      )
    );
  };

  const handleVariableDataChange = (key, newData) => {
    setIndependentVariables((prevVars) =>
      prevVars.map((variable) =>
        variable.key === key ? { ...variable, data: newData } : variable
      )
    );
  };

  const calculate = () => {
    // Parse data for independent variables
    const inputs = [];
    const outputs = dependentVariable.data.split(/[^0-9.-]/).filter(v => v).map(v => parseFloat(v))
    const numDataPoints = outputs.length;

    independentVariables.forEach((variable) => {
      const dataArray = variable.data.split(/[^0-9.-]/).filter(v => v).map(v => parseFloat(v))
      if (dataArray.length !== numDataPoints) {
        console.error(`Data length mismatch in variable ${variable.name}`);
        return;
      }
      inputs.push(dataArray);
    });

    // Transpose inputs to match the expected format
    const inputsTransposed = [];
    for (let i = 0; i < numDataPoints; i++) {
      inputsTransposed[i] = inputs.map((arr) => arr[i]);
    }

    // Parse data for dependent variable

    if (outputs.length !== numDataPoints) {
      console.error('Dependent variable data length mismatch');
      return;
    }

    // Perform multiple linear regression
    const { beta, predictedOutputs, rSquared } = multipleLinearRegression(inputs, outputs);
    console.log(beta)

    const points = outputs.map((output, index) => ({ x: output, y: predictedOutputs[index] }));
    setPoints(points)

    if (!beta) {
      console.error('Regression failed');
      return;
    }


    setResult({
      beta,
      rSquared,
      variableNames: independentVariables.map((v) => v.name),
      complete: true,
    });
    window.gtag('event', 'calculate-multiple-linear-regression');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid container justifyContent="center" xs={12} style={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            Multiple Linear Regression
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        {/* Independent Variables */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              {independentVariables.map((variable) => (
                <Grid item xs={Math.max(1, 12 / (independentVariables.length + 1))} key={variable.key}>
                  <Box display="flex" alignItems="center">
                    <TextField
                      name={`variableName-${variable.key}`}
                      fullWidth
                      label="Independent Variable Name"
                      value={variable.name}
                      onChange={(e) => handleVariableNameChange(variable.key, e.target.value)}
                    />
                    <IconButton
                      aria-label="remove variable"
                      onClick={() => removeIndependentVariable(variable.key)}
                      disabled={independentVariables.length === 1}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                  <Typography variant="body1">Values</Typography>
                  <TextareaAutosize
                    style={{ width: '100%' }}
                    aria-label="data"
                    minRows={7}
                    maxRows={20}
                    placeholder="Enter data..."
                    value={variable.data}
                    onChange={(e) => handleVariableDataChange(variable.key, e.target.value)}
                  />
                </Grid>
              ))}
              <Grid item xs={Math.max(12 / (independentVariables.length + 1))}>
                <TextField
                  name="dependentVariableName"
                  fullWidth
                  label="Dependent Variable Name"
                  value={dependentVariable.name}
                  onChange={(e) =>
                      setDependentVariable((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
                <Typography variant="body1">Values</Typography>
                <TextareaAutosize
                  style={{ width: '100%' }}
                  aria-label="data"
                  minRows={7}
                  maxRows={20}
                  placeholder="Enter data..."
                  value={dependentVariable.data}
                  onChange={(e) =>
                      setDependentVariable((prev) => ({ ...prev, data: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="outlined" onClick={addIndependentVariable} startIcon={<AddIcon />}>
                  Add Independent Variable
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>
                Calculate
              </Button>
            </Grid>
          </Paper>
        </Grid>
        {result.complete && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Grid container xs={12} justifyContent="center">
                <Typography variant="h5" gutterBottom>
                  <EqualizerIcon /> Multiple Linear Regression Results
                </Typography>
              </Grid>
              <Divider style={{ marginBottom: '20px' }} />
              <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Coefficients:
                </Typography>
                <Typography variant="body1">
                  Intercept (β₀): {toFixed(result.beta[0], 5)}
                </Typography>
                {result.beta.slice(1).map((coef, index) => (
                  <Typography variant="body1" key={index}>
                    β{index + 1} ({result.variableNames[index]}): {toFixed(coef, 5)}
                  </Typography>
                ))}
              </Box>
              <Divider style={{ marginBottom: '20px' }} />
              <Typography variant="body1" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                R²: {toFixed(result.rSquared, 5)}
              </Typography>
              <Divider style={{ marginBottom: '20px' }} />
              <Box display="flex" justifyContent="center" mb={2}>
                <Typography variant="body1">
                  <b>Equation:</b> {dependentVariable.name} = {toFixed(result.beta[0], 5)}
                  {result.beta.slice(1).map((coef, index) => {
                    const sign = coef >= 0 ? ' + ' : ' - ';
                    return `${sign}${toFixed(Math.abs(coef), 5)} * ${
                      result.variableNames[index]
                    }`;
                  })}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}
        {(result.complete && points && points.length > 0) && <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <ScatterPlot data={points} />
          </Paper>
        </Grid>}
      </Grid>
    </Container>
  );
}

export default function WrappedLinearRegression() {
  return (
    <AutoSaveProvider>
      <LinearRegression />
    </AutoSaveProvider>
  );
}
